"use client";

import { createContext, useContext, type ReactNode } from "react";
import type { CompetitionFeatures } from "./types";

interface CompetitionProfileContextValue {
    features: CompetitionFeatures;
    competitionId?: string;
}

const CompetitionProfileContext = createContext<CompetitionProfileContextValue | null>(null);

interface CompetitionProfileProviderProps {
    features: CompetitionFeatures;
    competitionId?: string;
    children: ReactNode;
}

export function CompetitionProfileProvider({
    features,
    competitionId,
    children,
}: CompetitionProfileProviderProps) {
    return (
        <CompetitionProfileContext.Provider value={{ features, competitionId }}>
            {children}
        </CompetitionProfileContext.Provider>
    );
}

/**
 * Access competition feature flags from any client component.
 * Throws if used outside a CompetitionProfileProvider.
 *
 * Usage:
 * ```tsx
 * const { features } = useCompetitionProfile();
 * {features.teamForm && <TeamForm />}
 * ```
 */
export function useCompetitionProfile(): CompetitionProfileContextValue {
    const context = useContext(CompetitionProfileContext);
    if (!context) {
        throw new Error("useCompetitionProfile must be used within a CompetitionProfileProvider");
    }
    return context;
}

/**
 * Optional variant — returns null when outside a CompetitionProfileProvider.
 * Use for shared components that may or may not be within a competition context.
 */
export function useCompetitionProfileOptional(): CompetitionProfileContextValue | null {
    return useContext(CompetitionProfileContext);
}
